import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { cleanRouterQuery, mapObjectToQueryString } from 'helpers/url';
import { ModalHouse } from 'components/molecules/ModalHouse';
import Container from 'components/layout/Grid/Container';
import Column from 'components/layout/Grid/Column';
import Icon from 'components/atoms/Icon/Icon';
import { HouseMapCursor } from './HouseMapCursor.partial';
import { MuseumPlanProps } from './MuseumPlan.types';
import { boxes } from './MuseumPlan.helper';
import {
  Wrapper,
  ToolBoxTopStyled,
  Toast,
  IconInfoStyled,
  ButtonCloseStyled,
  IconCloseStyled,
  ToolBoxBottomStyled,
  ButtonCenterStyled,
  ZoomButtonsGroupStyled,
  ButtonZoomInStyled,
  ButtonZoomOutStyled,
  ImageStyled,
  RectStyled,
} from './MuseumPlan.styles';

export const MuseumPlan: React.FC<MuseumPlanProps> = ({ className }) => {
  const { t, lang } = useTranslation('common');
  const router = useRouter();

  const housesDataMap = useRef(new Map());

  const houseQueryParameterName = 'h';

  const initialScale = 0.5;
  const mapLocationCursorScale = 1.5;
  const svgBackground = {
    width: 5000,
    height: 1386,
  };

  const Viewer = useRef<ReactZoomPanPinchRef>(null);
  const [zoom, setZoom] = useState(initialScale);
  const [showToaster, setShowToaster] = useState(true);
  const [stateShowModal, setStateShowModal] = useState(false);
  const [stateHouseNr, setStateHouseNr] = useState<string | null>(null);

  const getHouseNrFromQuery = () => {
    if (typeof window === 'undefined') {
      return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(houseQueryParameterName);
  };

  useEffect(() => {
    const houseNrQuery = getHouseNrFromQuery();
    if (houseNrQuery) {
      setStateHouseNr(houseNrQuery);
      setStateShowModal(true);
    }
  }, []);

  function computeCursorPosition(houseNummer: string): void {
    let translate = '0 0';
    if (houseNummer) {
      const box = boxes.find((box) => box.house_nr === houseNummer);
      if (box) {
        const x = Math.round(parseInt(box.x, 10)) / mapLocationCursorScale;
        const y = Math.round(parseInt(box.y, 10)) / mapLocationCursorScale - 50;
        translate = `${x} ${y}`;
      }
    }

    const transform = `scale(${mapLocationCursorScale}) translate(${translate})`;
    const cursor = document.getElementById('locationCursor');
    if (cursor) {
      cursor.style.display = 'block';
      cursor.setAttribute('transform', transform);
    }
  }

  useEffect(() => {
    // On change zoom -> set position
    if (Viewer.current) {
      Viewer.current.setTransform(
        //calculate x and y
        Viewer.current.state.positionX,
        Viewer.current.state.positionY,
        zoom
      );
    }
  }, [zoom]);

  return (
    <Container>
      <Column span={12}>
        <Wrapper className={className}>
          <TransformWrapper
            ref={Viewer}
            centerZoomedOut={false}
            minScale={0.25}
            maxScale={1}
            initialScale={initialScale}
            limitToBounds={false}
            zoomAnimation={{ disabled: true }}
            alignmentAnimation={{ disabled: true }}
            onInit={(event) => {
              // set cursor position in houseNr query parameter exist
              const houseNrQuery = getHouseNrFromQuery();
              if (houseNrQuery) {
                const box = boxes.find((box) => box.house_nr === houseNrQuery);
                if (box && event && event) {
                  const px = (-box.x + 100) * initialScale;
                  const py = (-box.y + 200) * initialScale;
                  event.setTransform(px, py, initialScale, 1000);
                }
                computeCursorPosition(houseNrQuery);
              }
            }}
          >
            {({ resetTransform }) => {
              return (
                <>
                  {showToaster && (
                    <ToolBoxTopStyled>
                      <Toast>
                        <IconInfoStyled name="INFO" />
                        <p>{t('toastMuseumClick')}</p>
                        <ButtonCloseStyled
                          onClick={() => {
                            setShowToaster(false);
                          }}
                        >
                          <IconCloseStyled name="CLOSE" />
                        </ButtonCloseStyled>
                      </Toast>
                    </ToolBoxTopStyled>
                  )}
                  <ToolBoxBottomStyled>
                    <ButtonCenterStyled
                      onClick={() => {
                        resetTransform();
                      }}
                    >
                      <Icon name="CENTERMAP" />
                    </ButtonCenterStyled>

                    <ZoomButtonsGroupStyled>
                      <ButtonZoomInStyled
                        onClick={() => {
                          setZoom((prev) => prev + 0.1);
                        }}
                      >
                        <Icon name="ZOOMIN" />
                      </ButtonZoomInStyled>
                      <ButtonZoomOutStyled
                        onClick={() => {
                          setZoom((prev) => prev - 0.1);
                        }}
                      >
                        <Icon name="ZOOMOUT" />
                      </ButtonZoomOutStyled>
                    </ZoomButtonsGroupStyled>
                  </ToolBoxBottomStyled>
                  <TransformComponent wrapperStyle={{ maxHeight: '500px', position: 'relative' }}>
                    <svg width={svgBackground.width} height={svgBackground.height}>
                      <ImageStyled
                        href="/map/map-temp_1.7mb.png"
                        x="0"
                        y="0"
                        height={svgBackground.height}
                        width={svgBackground.width}
                      />

                      <HouseMapCursor />

                      {boxes.map((box) => (
                        <RectStyled
                          id={`house-${box.house_nr}`}
                          data-house-nr={box.house_nr}
                          key={`house-${box.house_nr}`}
                          x={box.x}
                          y={box.y}
                          height="28"
                          width={box.width}
                          fill={'transparent'}
                          fillOpacity="0.44"
                          onClick={(event) => {
                            if (showToaster) {
                              setShowToaster(false);
                            }
                            // on select a house -> open modal and place cursor
                            if (
                              event.currentTarget &&
                              event.currentTarget.getAttribute('data-house-nr') !== null
                            ) {
                              const houseNummer = event.currentTarget.getAttribute('data-house-nr');
                              if (houseNummer) {
                                computeCursorPosition(houseNummer);
                                setStateHouseNr(houseNummer);
                                setStateShowModal(true);

                                // replace house-nr in url query param
                                const queryObj = cleanRouterQuery(router.query);
                                queryObj.h = houseNummer;
                                const queryString = mapObjectToQueryString(queryObj);
                                router.push(
                                  `/${lang}${router.asPath.split('?')[0]}${queryString}`,
                                  undefined
                                );
                              }
                            }
                          }}
                        />
                      ))}
                    </svg>
                  </TransformComponent>
                </>
              );
            }}
          </TransformWrapper>

          {stateShowModal && stateHouseNr && (
            <ModalHouse
              dataMap={housesDataMap.current}
              houseNr={stateHouseNr}
              onCloseCallback={() => {
                setStateShowModal(false);
                const cursor = document.getElementById('locationCursor');
                if (cursor) {
                  cursor.style.display = 'none';
                }
              }}
            />
          )}
        </Wrapper>
      </Column>
    </Container>
  );
};
